import { applicantStore } from '@/store'
import axios, { CancelTokenSource } from 'axios'
import { ErrorUtils } from '@/commons/utils/error'
import { StringUtils } from '@/commons/utils/string'
import { ENVIRONMENT } from '@/commons/configuration'
import { RequestGeneralError, GeneralError } from '@/store/entities'
import authenticatedAxios from '@/commons/configuration/authenticated.axios'

function getCancelToken (): CancelTokenSource {
    if (!applicantStore.requestCancelToken) {
        applicantStore.requestCancelToken = axios.CancelToken.source()
    }
    return applicantStore.requestCancelToken
}

export class PaymentService {
    static async fetchStateFee (uuid: string | null | undefined): Promise<void> {
        if (!uuid) {
            throw new GeneralError([
                'errors.system.stateFee.missingUuid'
            ])
        }
        try {
            const stateFee = await authenticatedAxios.get(ENVIRONMENT.stateFeeUrl, {
                params: {
                    uuid: uuid
                },
                cancelToken: getCancelToken().token
            })
            applicantStore.stateFee = stateFee.data
        } catch (e) {
            throw ErrorUtils.resolveRequestErrorEntity(e)
        }
    }

    static async confirmConditions (applicationUuid: string): Promise<void> {
        const url = ENVIRONMENT.confirmConditionsUrl.replace('{APPLICATION_UUID}', applicationUuid)
        try {
            await authenticatedAxios.post(url, null, {
                cancelToken: getCancelToken().token
            })
        } catch {
            ErrorUtils.requestErrorHandler(new RequestGeneralError(
                ['errors.request.validation.message']))
        }
    }

    static async confirmValidDocumentForCourier (applicationUuid: string): Promise<void> {
        const url = ENVIRONMENT.confirmValidDocumentUrl.replace('{APPLICATION_UUID}', applicationUuid)
        try {
            await authenticatedAxios.post(url, null, {
                cancelToken: getCancelToken().token
            })
        } catch {
            ErrorUtils.requestErrorHandler(new RequestGeneralError(
                ['errors.request.validation.message']
            ))
        }
    }

    private static async startPayment (bank: string, applicationUuid: string, cancelToken: CancelTokenSource): Promise<void> {
        const isEveryPay = bank === 'everypay'
        const paymentUrl = isEveryPay ? ENVIRONMENT.everyPayPaymentUrl : ENVIRONMENT.paymentUrl
        const params = {
            uuid: applicationUuid,
            bank: bank.toUpperCase()
        }

        try {
            const response = await authenticatedAxios.get(paymentUrl, {
                params: params,
                cancelToken: cancelToken.token
            })

            if (isEveryPay) {
                window.location.href = response.data.paymentLink
            } else {
                const form = StringUtils.stringToHtmlElement(response.data.form) as HTMLFormElement
                document.body.appendChild(form)
                form.submit()
            }
        } catch {
            ErrorUtils.requestErrorHandler(new RequestGeneralError(
                ['errors.request.validation.message']))
        }
    }

    static async confirmAndStartPayment (bank: string): Promise<void> {
        const applicationUuid = applicantStore.application.uuid
        if (!applicationUuid) {
            ErrorUtils.requestErrorHandler(new RequestGeneralError(
                ['errors.request.server.message']
            ))
            return
        }
        if (applicantStore.requestCancelToken) {
            applicantStore.requestCancelToken.cancel()
        }
        applicantStore.requestCancelToken = axios.CancelToken.source()
        await this.startPayment(bank, applicationUuid, getCancelToken())
        applicantStore.requestCancelToken = null
    }
}
